import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import {
	AlarisComplexTableComponent,
	AlarisDialogService,
	AlarisFilterManagerComponent,
	AlarisTableStateService,
	EditPanelWidth,
	RowActionSimple,
	TABLE_STATE_SERVICE_INJECTOR,
	TableEntityField
} from '@campaign-portal/components-library';
import { Product, Rule } from '@helpers/types/models';
import { FilterType, Paging } from '@campaign-portal/namespace/common/rpc.params';
import { BehaviorSubject } from 'rxjs';
import { TableSettingsService } from '@helpers/api/table-settings.service';
import { TableTypes } from '@campaign-portal/namespace/entities/table-settings/specs';
import { RulesService } from './rules.service';
import { RulesFieldsService } from './rules.fields.service';
import { EditRuleComponent } from './edit-rule/edit-rule.component';
import { RulesDeleteDialogComponent } from './dialog/dialog.component';
import { ProductsService } from '@helpers/repo/products.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RepoService, RepoType } from '@helpers/repo/repo.service';
import { UserService } from '../../auth/user.service';
import { EntityField, InputComplexType, InputType } from '@campaign-portal/namespace/common/entityField';
import { exist, Id } from '@campaign-portal/namespace/common/id';
import { eapiEnable } from '../../app.module';

enum FilterVariables {
	ID = 'ruleId',
	NAME = 'name',
	CHOICE_LIST = 'choiceListName',
	PREFIX_LIST = 'prefixList',
	PRODUCT_LIST = 'productList',
	PRODUCTS_TYPE = 'productsType',
	PRODUCTS_NAMES = 'productsNames'
}
const id = new TableEntityField({
	id: null,
	name: 'rules.id',
	variable: FilterVariables.ID,
	filterType: FilterType.LIKE,
	required: false,
	type: InputType.TEXT
});
const name = new TableEntityField({
	id: null,
	name: 'rules.name',
	variable: FilterVariables.NAME,
	filterType: FilterType.LIKE,
	required: false,
	type: InputType.TEXT
});
const choiceListName = new TableEntityField({
	id: null,
	name: 'rules.choice_list',
	variable: FilterVariables.CHOICE_LIST,
	filterType: FilterType.LIKE,
	required: false,
	type: InputType.TEXT
});
const prefixList = new TableEntityField({
	id: null,
	name: 'rules.prefix_list',
	variable: FilterVariables.PREFIX_LIST,
	filterType: FilterType.LIKE,
	required: false,
	type: InputType.TEXT
});
const productList = new TableEntityField({
	id: null,
	name: 'rules.product_list',
	variable: FilterVariables.PRODUCT_LIST,
	filterType: FilterType.LIKE,
	required: false,
	type: InputComplexType.MULTISELECT,
	data: []
});
const productsTypes = new TableEntityField( {
	id: null,
	name: 'rules.product_type',
	variable: FilterVariables.PRODUCTS_TYPE,
	filterType: FilterType.LIKE,
	required: false,
	type: InputComplexType.SELECT,
	data: ['all', 'hlr', 'sms']
});
const productsNames = new TableEntityField( {
	id: null,
	name: 'rules.product_names',
	variable: FilterVariables.PRODUCTS_NAMES,
	filterType: FilterType.LIKE,
	required: false,
	type: InputComplexType.MULTISELECT,
	data: []
});
@Component({
	selector: 'app-rules',
	templateUrl: './rules.component.html',
	styleUrls: [
		'../../../../node_modules/@campaign-portal/components-library/src/assets/templates/table-complex/complex-table.component.scss',
		'rules.component.scss'
	],
	providers: [
		AlarisTableStateService,
		{ provide: TABLE_STATE_SERVICE_INJECTOR, useExisting: AlarisTableStateService }
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class RulesComponent extends AlarisComplexTableComponent<Rule> implements OnDestroy, AfterViewInit {
	private headers: EntityField[] = [];
	private productsMap= new Map<any, any>;
	override readonly rowActions: RowActionSimple<any>[] = [
		{
			icon: 'icon-edit',
			label: 'actions.edit',
			action: this.edit.bind(this),
			disabled: (): boolean => {
				return !this.user.allowed(['rules.edit'])
			}
		},
		{
			icon: 'icon-delete',
			label: 'actions.delete',
			action: this.delete.bind(this),
			highlight: true,
			separator: true,
			disabled: (): boolean => {
				return !this.user.allowed(['rules.edit'])
			}
		},
		{
			icon: 'icon-copy',
			label: 'actions.clone',
			action: this.clone.bind(this),
			highlight: true,
			separator: true,
			disabled: (): boolean => {
				return !this.user.allowed(['rules.edit'])
			}
		}
	];
	override readonly dblClickRowAction = this.edit;
	override readonly pageSizeOptions = [25, 50, 100, 200];
	override readonly paging: Paging = {
		Skip: this.activePage,
		Take: this.pageSize
	};


	readonly exporting$ = new BehaviorSubject<boolean>(false);

	constructor(
		dataService: RulesService,
		public override readonly fieldsService: RulesFieldsService,
		tableSettingsService: TableSettingsService,
		public readonly repo: RepoService,
		public readonly productsService: ProductsService,
		private readonly dialog: AlarisDialogService,
		private readonly rulesService: RulesService,
		protected readonly user: UserService,
	) {
		super(
			dataService,
			fieldsService,
			tableSettingsService,
			TableTypes.INTERNAL
		);
		this.headers = [id, name, choiceListName, prefixList, productList, productsTypes, productsNames];
		this.state.filters()
			.set(FilterVariables.ID, { enabled: true, filterType: FilterType.LIKE })
			.set(FilterVariables.NAME, { enabled: true, filterType: FilterType.LIKE })
			.set(FilterVariables.CHOICE_LIST, { enabled: true, filterType: FilterType.LIKE })
			.set(FilterVariables.PREFIX_LIST, { enabled: true, filterType: FilterType.LIKE })
			.set(FilterVariables.PRODUCT_LIST, { enabled: true, value: [], filterType: FilterType.LIKE })
			.set(FilterVariables.PRODUCTS_TYPE, { enabled: true, filterType: FilterType.LIKE })
			.set(FilterVariables.PRODUCTS_NAMES, { enabled: true, value: [], filterType: FilterType.LIKE })
		this.repo.load(['hlr', 'sms'], ['HTTP', 'ENUM', 'SS7', 'MNP']).pipe(takeUntilDestroyed()).subscribe();
	}

	override ngOnDestroy(): void {
		super.ngOnDestroy();
		this.exporting$.next(false);
		this.exporting$.complete();
	}

	edit(rule?: Rule): void {
		this.editPanel.open<EditRuleComponent, { rule: Rule }, boolean>(EditRuleComponent, EditPanelWidth.SM, { rule })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if ( result ) {
					this.load();
				}
			});
	}

	delete(rule: Rule): void {
		this.dialog.open<boolean>(RulesDeleteDialogComponent, { autoFocus: false, data: { rule } })
			.closed
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if ( result ) {
					this.load();
				}
			});
	}

	export(): void {
		this.exporting$.next(true);
		this.rulesService.export(this.total, this.state.readParams)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.exporting$.next(false);
			});
	}

	clone(rule: Rule): void {
		const tmpRule = JSON.parse(JSON.stringify(rule));
		tmpRule.ruleId = null;
		tmpRule.name = rule.name + ' \u2013 Clone';
		this.editPanel.open<EditRuleComponent, { rule: Rule }, boolean>(
			EditRuleComponent,
			EditPanelWidth.SM,
			{ rule: tmpRule, afterClone: true }
		)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if ( result ) {
					this.load();
				}
			});
	}

	override editTableFilter(): void {
		this.editPanel.open(AlarisFilterManagerComponent, EditPanelWidth.MD, {
			filtersChange: this.filtersChange,
			filters: this.state.filters,
			tHeaders: this.headers
		});
	}

	transform(id: Id<exist> | string): Product | null {
		if ( typeof id === 'string' ) {
			id = parseInt(id, 10);
		}
		return this.transformProduct(id) as (Product | null);
	}

	private transformProduct(id: Id<exist>): Product | null {
		return this.productsService.find<Product>(id, this.productsMap, RepoType.PRODUCT);
	}

	override ngAfterViewInit(): void {
		super.ngAfterViewInit();
		if (!eapiEnable) {
			productList.type = InputType.TEXT;
			productsNames.type = InputType.TEXT;
		}
		this.productsService.map$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(list => {
				this.productsMap = list;
			});
		this.productsService.clientProductsNames$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(list => {
				if (eapiEnable) {
					if (list) {
						productsNames.data = list;
						this.setRefBooksInFields({
							productsNames:
								{ list$: new BehaviorSubject<string[]>(list),
									loading$: this.fieldsService.loading$
								},
						});
						productsNames.type = InputComplexType.MULTISELECT;
						this.state.filters()
							.set(FilterVariables.PRODUCTS_NAMES,
								{ enabled: true, value: [], filterType: FilterType.LIKE });
					}
				} else {
					productsNames.type = InputType.TEXT;
					this.state.filters()
						.set(FilterVariables.PRODUCTS_NAMES,
							{ enabled: true, value: null, filterType: FilterType.LIKE });
				}
			});
		this.productsService.clientProducts$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((list) => {
				if (eapiEnable) {
					if (list.length > 0) {
						const data = list.map((item) => {
							return { name: item.product_caption
									+ ' (' + item.product_id + ')', value: item.product_id };
						});
						productList.data = data;
						this.setRefBooksInFields({
							productList:
								{ list$: new BehaviorSubject<{ name: string; value: number }[]>(data),
									loading$: this.fieldsService.loading$
								}
						});
						productList.type = InputComplexType.MULTISELECT;
						this.state.filters()
							.set(FilterVariables.PRODUCT_LIST,
								{ enabled: true, value: [], filterType: FilterType.LIKE });
					}
				} else {
					productList.type = InputType.TEXT;
					this.state.filters()
						.set(FilterVariables.PRODUCT_LIST,
							{ enabled: true, value: null, filterType: FilterType.LIKE });
				}
			});
	}

	protected readonly eapiEnable = eapiEnable;
}
